import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowUpRight, BadgeCheck, ShieldCheck,
  Zap, Globe, Radio, LineChart, RefreshCcw,
} from "lucide-react";
import { motion } from "framer-motion";
import WalletQR, { buildWalletUri } from "../components/WalletQR";
import Footer from "../components/Footer";
import { TrendTicker as CoinTicker } from "../components/TrendTicker";
import Seo from "../components/Seo";
const Testimonials = React.lazy(() => import("../components/Testimonials"));
import { COINS_NETWORKS, COIN_COUNT, NETWORK_COUNT } from "../lib/facts";
import api from "../lib/api";
import { LANDING_FAQ } from "../lib/landingFaq";
import { BUSINESS_PAGES } from "../lib/seoContent";

const HERO_IMG = "/hero-blockchain.webp";
const SHIELD_IMG = "/brand/custody-shield.webp";
const DOTS_IMG = "https://images.unsplash.com/photo-1683447551794-1c287cd42675?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwzfHxkYXJrJTIwYWJzdHJhY3QlMjBub2RlcyUyMGdsYXNzJTIwbmV0d29ya3xlbnwwfHx8fDE3ODUwODIwNTd8MA&ixlib=rb-4.1.0&q=60&w=1600";

const fadeUp = {
  initial: { opacity: 0, y: 36 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.85, ease: [0.16, 1, 0.3, 1] },
};

const Overline = ({ children, className = "" }) => (
  <div className={`font-mono text-[11px] uppercase tracking-[0.25em] text-neutral-500 ${className}`}>
    {children}
  </div>
);

/* ── Pill CTAs ─────────────────────────────────────────────────────── */
function Cta({ to, children, testId, solid = false, className = "" }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className={`inline-flex items-center justify-center gap-2.5 text-[14px] font-bold lowercase h-[52px] rounded-full transition-colors duration-300 ${
        solid
          ? "w-full sm:w-auto px-8 bg-white text-black hover:bg-neutral-200"
          : "flex-1 sm:flex-none whitespace-nowrap px-4 sm:px-8 border border-white/20 text-white hover:bg-white/10"
      } ${className}`}
    >
      {children}
    </Link>
  );
}

/* ── Claim form — pill capsule with live availability check ────────── */
function ClaimForm({ testPrefix = "hero" }) {
  const [name, setName] = useState("");
  const [avail, setAvail] = useState(null);

  useEffect(() => {
    const u = name.trim().toLowerCase();
    if (u.length < 3) { setAvail(null); return undefined; }
    let live = true;
    const t = setTimeout(() => {
      api.get("/username/available", { params: { username: u } })
        .then((r) => { if (live) setAvail({ name: u, state: r.data.available ? "available" : r.data.reason || "taken" }); })
        .catch(() => { if (live) setAvail(null); });
    }, 400);
    return () => { live = false; clearTimeout(t); };
  }, [name]);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const u = e.target.username.value.trim().toLowerCase();
        window.location.href = `/signup${u ? `?username=${encodeURIComponent(u)}` : ""}`;
      }}
      className="w-full max-w-md mx-auto"
      data-testid={`${testPrefix}-claim-form`}
    >
      <div className="flex flex-row items-center gap-2 rounded-full glass-panel-sm border-white/30 bg-white/[0.08] shadow-[0_0_50px_rgba(255,255,255,0.05)] p-1.5 pl-4 sm:pl-6 focus-within:border-white/50 transition-colors">
        <div className="flex items-center flex-1 min-w-0">
          <span className="shrink-0 font-mono text-[14px] sm:text-[13px] text-neutral-500 select-none">crypt.pe/</span>
          <input
            name="username"
            placeholder="yourname"
            data-testid={`${testPrefix}-claim-input`}
            autoComplete="off"
            spellCheck={false}
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="flex-1 min-w-0 h-11 bg-transparent border-0 outline-none font-mono text-[16px] sm:text-[14px] text-white placeholder:text-neutral-600 focus:placeholder:text-transparent pl-0.5"
          />
        </div>
        <button
          type="submit"
          data-testid={`${testPrefix}-claim-submit`}
          className="shrink-0 inline-flex items-center justify-center gap-1.5 bg-white text-black text-[13px] font-bold lowercase px-5 sm:px-6 h-11 rounded-full hover:bg-neutral-200 active:scale-[0.98] transition-[background-color,transform]"
        >
          claim <ArrowRight size={13} />
        </button>
      </div>
      <div className="h-5 mt-2 font-mono text-[11px] lowercase text-center" data-testid={`${testPrefix}-claim-availability`} aria-live="polite">
        {avail?.state === "available" && <span className="text-emerald-400">crypt.pe/{avail.name} is available ✓</span>}
        {avail?.state === "taken" && <span className="text-neutral-500">crypt.pe/{avail.name} is already claimed — try another</span>}
        {avail && avail.state !== "available" && avail.state !== "taken" && <span className="text-neutral-500">that handle can't be used — try another</span>}
      </div>
    </form>
  );
}

/* ── Chain ticker — shared live movers marquee (see components/TrendTicker) ── */

/* ── Live payment page preview (glass) ─────────────────────────────── */
const DEMO = {
  eth: { label: "ETH",  address: "0x71C7656EC7ab88b098defB751B7401B5f6d8976F", amount: "0.042 ETH" },
  usdt: { label: "USDT", address: "0x71C7656EC7ab88b098defB751B7401B5f6d8976F", amount: "120.00 USDT" },
  btc: { label: "BTC",  address: "bc1qxy2kgdygjrsqtzq2n0yrf2493p83kkfjhx0wlh", amount: "0.0021 BTC" },
  sol: { label: "SOL",  address: "DYw8jCTfwHNRJhhmFcbXvVDTqWMEVFBX6ZKUmG5CNSKK", amount: "1.8 SOL" },
};

function PaymentPreview() {
  const [coin, setCoin] = useState("eth");
  const d = DEMO[coin];
  return (
    <div
      data-testid="hero-preview-card"
      className="relative mx-auto w-full max-w-[360px] rounded-3xl glass-panel overflow-hidden shadow-[0_60px_120px_-30px_rgba(0,0,0,0.9)]"
    >
      <div className="px-6 pt-6 pb-4 flex items-center gap-3.5 border-b border-white/10">
        <div className="w-10 h-10 rounded-full bg-white text-black flex items-center justify-center font-bold text-[13px]">MA</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="font-semibold text-[14px] tracking-tight text-white">Maya Studio</span>
            <BadgeCheck size={14} className="text-emerald-400 shrink-0" />
          </div>
          <div className="font-mono text-[11px] text-neutral-500">crypt.pe/maya</div>
        </div>
        <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-emerald-400 border border-emerald-400/25 bg-emerald-400/10 rounded-full px-2.5 py-1">live</span>
      </div>
      <div className="px-6 pt-4">
        <div className="flex gap-1.5" data-testid="preview-coin-tabs">
          {Object.entries(DEMO).map(([k, v]) => (
            <button
              key={k}
              data-testid={`preview-coin-${k}`}
              onClick={() => setCoin(k)}
              aria-pressed={coin === k}
              className={`flex-1 font-mono text-[10px] uppercase tracking-wider py-2 rounded-full transition-colors ${
                coin === k ? "bg-white text-black" : "bg-white/5 text-neutral-500 hover:bg-white/10 hover:text-neutral-300"
              }`}
            >
              {v.label}
            </button>
          ))}
        </div>
      </div>
      <div className="px-6 py-5 flex flex-col items-center">
        <div className="rounded-2xl p-3 bg-white">
          <WalletQR value={buildWalletUri(coin, d.address)} size={150} testId="preview-qr" />
        </div>
        <div className="mt-4 w-full flex items-center justify-between rounded-full bg-white/5 border border-white/10 px-4 py-2.5">
          <span className="font-mono text-[11px] text-neutral-400 truncate">
            {d.address.slice(0, 10)}…{d.address.slice(-6)}
          </span>
          <span className="font-mono text-[12px] font-semibold text-white shrink-0 ml-3">{d.amount}</span>
        </div>
      </div>
      <div className="px-6 py-3.5 border-t border-white/10 flex items-center gap-1.5 text-[11px] text-neutral-500">
        <ShieldCheck size={12} className="text-emerald-400" />
        settles to her wallet — never ours
      </div>
    </div>
  );
}

/* ── Bento tile shell ──────────────────────────────────────────────── */
function Bento({ className = "", children, testId }) {
  return (
    <motion.div
      {...fadeUp}
      data-testid={testId}
      className={`relative rounded-3xl bg-[#0A0A0A] border border-white/10 overflow-hidden hover:border-white/25 transition-colors duration-500 ${className}`}
    >
      {children}
    </motion.div>
  );
}

/* ── Code editor window ────────────────────────────────────────────── */
function CodeWindow() {
  return (
    <div className="rounded-3xl bg-[#070707] border border-white/10 overflow-hidden shadow-[0_50px_100px_-30px_rgba(0,0,0,0.9)]" data-testid="dev-code-window">
      <div className="flex items-center gap-2 px-5 py-3.5 border-b border-white/10">
        <span className="mac-dot bg-[#FF5F57]" />
        <span className="mac-dot bg-[#FEBC2E]" />
        <span className="mac-dot bg-[#28C840]" />
        <span className="ml-3 font-mono text-[11px] text-neutral-600">create_order.sh</span>
      </div>
      <pre className="p-6 font-mono text-[12.5px] leading-[1.9] overflow-x-auto">
        <code>
          <span className="text-neutral-600"># create a tracked on-chain order</span>{"\n"}
          <span className="text-emerald-400">curl</span> <span className="text-neutral-300">-X POST https://crypt.pe/api/v1/payments \</span>{"\n"}
          <span className="text-neutral-300">  -H </span><span className="text-amber-200/80">"Authorization: Bearer sk_live_····"</span> <span className="text-neutral-300">\</span>{"\n"}
          <span className="text-neutral-300">  -d </span><span className="text-amber-200/80">{`'{ "amount_usd": 49.99,`}</span>{"\n"}
          <span className="text-amber-200/80">{`       "accepted_coins": ["usdc"] }'`}</span>{"\n\n"}
          <span className="text-neutral-600"># → hosted checkout + hmac-signed webhooks</span>{"\n"}
          <span className="text-neutral-400">{`{ `}</span><span className="text-sky-300">"checkout_url"</span><span className="text-neutral-400">: </span><span className="text-amber-200/80">"https://crypt.pe/checkout/ord_7f2…"</span><span className="text-neutral-400">,</span>{"\n"}
          <span className="text-neutral-400">  </span><span className="text-sky-300">"status"</span><span className="text-neutral-400">: </span><span className="text-amber-200/80">"awaiting_payment"</span><span className="text-neutral-400">{` }`}</span>
        </code>
      </pre>
    </div>
  );
}

function usd(v) {
  if (v == null) return "—";
  if (v >= 1_000_000) return "$" + (v / 1_000_000).toFixed(1) + "M";
  if (v >= 1_000) return "$" + (v / 1_000).toFixed(1) + "K";
  return "$" + Math.floor(v);
}
function compact(v) {
  if (v == null) return "—";
  if (v >= 1_000_000) return (v / 1_000_000).toFixed(1) + "M";
  if (v >= 1_000) return (v / 1_000).toFixed(1) + "K";
  return String(v);
}

const STEPS = [
  { n: "01", t: "claim your handle", c: "pick crypt.pe/you — takes less time than making coffee. no identity checks, no forms, no waiting room." },
  { n: "02", t: "paste your addresses", c: `wallets you already own. btc, evm, solana, tron — display any of ${COIN_COUNT} coins across ${NETWORK_COUNT} networks.` },
  { n: "03", t: "share one link", c: "customers scan and pay from most wallets. we track it on-chain, receipt it, and notify you." },
];

/* ── Compact mid-page CTA — repeats the two journeys ───────────────── */
function MidCta({ id }) {
  return (
    <div className="border-t border-white/10" data-testid={`mid-cta-${id}`}>
      <div className="max-w-7xl mx-auto px-5 md:px-12 py-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-neutral-500">already convinced? pick your path</span>
        <div className="flex flex-row gap-1.5 sm:gap-2.5">
          <Link to="/signup" data-testid={`mid-cta-${id}-link`}
            className="inline-flex items-center justify-center gap-1 sm:gap-2 whitespace-nowrap text-[10px] min-[360px]:text-[11px] sm:text-[13px] font-bold lowercase bg-white text-black rounded-full px-2.5 min-[360px]:px-3 sm:px-6 h-10 sm:h-11 hover:bg-neutral-200 transition-colors">
            get paid with a link <ArrowRight size={13} className="hidden sm:inline" />
          </Link>
          <Link to="/crypto-payment-gateway" data-testid={`mid-cta-${id}-gateway`}
            className="inline-flex items-center justify-center whitespace-nowrap text-[10px] min-[360px]:text-[11px] sm:text-[13px] font-bold lowercase bg-white/[0.07] border border-white/30 text-white rounded-full px-2.5 min-[360px]:px-3 sm:px-6 h-10 sm:h-11 hover:bg-white/15 transition-colors">
            accept crypto on my site
          </Link>
        </div>
      </div>
    </div>
  );
}


export default function Landing() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/stats/landing").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  return (
    <main data-testid="landing-page" className="page-grain bg-black text-neutral-100 overflow-x-hidden mt-[calc(-1*env(safe-area-inset-top,0px))]">
      <Seo
        title="Get Paid in Crypto — 0% Fees, Your Own Wallet"
        description={`Non-custodial crypto payment gateway. Accept BTC, ETH, USDT, USDC, SOL and ${COIN_COUNT} coins with 0% transaction fees — funds settle directly to your own wallet.`}
        keywords="crypto payment link, non-custodial payment gateway, accept crypto payments, 0% fee crypto gateway"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: LANDING_FAQ.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }}
      />

      {/* ══ 1 · HERO ══════════════════════════════════════════════ */}
      <section className="relative min-h-[100dvh] flex items-center justify-center overflow-hidden">
        <img
          src={HERO_IMG}
          alt="dark chrome abstract background representing the crypt.pe payment network"
          width="1264"
          height="848"
          fetchPriority="high"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
          style={{ maskImage: "radial-gradient(80% 80% at 50% 40%, black 30%, transparent 100%)", WebkitMaskImage: "radial-gradient(80% 80% at 50% 40%, black 30%, transparent 100%)" }}
        />
        <div aria-hidden className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-black" />

        <div className="relative max-w-5xl mx-auto px-5 md:px-12 w-full text-center pt-36 pb-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
          >
            <div data-testid="hero-eyebrow" className="inline-flex flex-wrap items-center justify-center gap-2.5 rounded-full glass-panel px-4 sm:px-5 py-2 font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.18em] sm:tracking-[0.25em] text-neutral-400 text-center">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse shrink-0" />
              <span className="sm:hidden">non-custodial · {COIN_COUNT} coins</span>
              <span className="hidden sm:inline">non-custodial · {COINS_NETWORKS}</span>
            </div>
          </motion.div>

          <h1
            data-testid="hero-title"
            className="mt-10 text-6xl sm:text-7xl lg:text-[6.75rem] font-black lowercase tracking-tighter leading-[0.92] text-white"
          >
            {["get", "paid."].map((w, i) => (
              <motion.span
                key={w}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.35 + i * 0.1, ease: [0.16, 1, 0.3, 1] }}
                className="inline-block mr-[0.22em]"
              >
                {w}
              </motion.span>
            ))}
            <br />
            {["keep", "everything."].map((w, i) => (
              <motion.span
                key={w}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.6 + i * 0.1, ease: [0.16, 1, 0.3, 1] }}
                className="inline-block mr-[0.22em] text-neutral-500"
              >
                {w}
              </motion.span>
            ))}
          </h1>

          <motion.p
            data-testid="hero-subtitle"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9 }}
            className="mt-8 text-base md:text-lg text-neutral-400 leading-relaxed max-w-xl mx-auto"
          >
            the payment layer that never touches your money. one link,
            most wallets, zero platform fee — forever.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05 }}
            className="mt-12"
          >
            <ClaimForm testPrefix="hero" />
            <div className="mt-5 flex flex-row flex-nowrap items-center justify-center gap-1.5 sm:gap-2.5 px-0.5" data-testid="hero-two-paths">
              <Link to="/signup" data-testid="hero-path-link"
                className="inline-flex items-center justify-center gap-1 sm:gap-2 whitespace-nowrap font-mono text-[9.5px] min-[360px]:text-[10.5px] sm:text-[12px] lowercase text-zinc-300 hover:text-white transition-colors border border-white/15 rounded-full px-2.5 min-[360px]:px-3 sm:px-5 py-2 sm:py-2.5 min-h-[44px] sm:min-h-0 hover:bg-white/10">
                get paid with a link <ArrowRight size={11} className="hidden sm:inline" />
              </Link>
              <Link to="/crypto-payment-gateway" data-testid="hero-path-gateway"
                className="inline-flex items-center justify-center gap-1 sm:gap-2 whitespace-nowrap font-mono text-[9.5px] min-[360px]:text-[10.5px] sm:text-[12px] lowercase text-zinc-300 hover:text-white transition-colors border border-white/15 rounded-full px-2.5 min-[360px]:px-3 sm:px-5 py-2 sm:py-2.5 min-h-[44px] sm:min-h-0 hover:bg-white/10">
                accept crypto on my site <ArrowRight size={11} className="hidden sm:inline" />
              </Link>
            </div>
            <div className="mt-6 flex items-center justify-center gap-6 font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500" data-testid="hero-trust-row">
              <span>free forever</span>
              <span className="w-1 h-1 rounded-full bg-neutral-800" />
              <span>no card</span>
              <span className="w-1 h-1 rounded-full bg-neutral-800" />
              <span>60s setup</span>
            </div>
            <p className="mt-4 font-mono text-[10.5px] text-neutral-400" data-testid="hero-fee-clarifier">
              0% crypt.pe transaction fee. network fees &amp; optional plan fees still apply —{" "}
              <Link to="/fees" data-testid="hero-fees-link" className="text-neutral-400 hover:text-white underline underline-offset-2 transition-colors">see all fees</Link>
            </p>
          </motion.div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 font-mono text-[10px] uppercase tracking-[0.35em] text-neutral-600 animate-pulse hidden md:block">
          scroll
        </div>
      </section>

      {/* ══ 2 · TICKER ════════════════════════════════════════════ */}
      <CoinTicker />

      {/* ══ 3 · BENTO GRID ════════════════════════════════════════ */}
      <section className="py-24 md:py-40">
        <div className="max-w-7xl mx-auto px-5 md:px-12">
          <motion.div {...fadeUp}>
            <Overline>the platform</Overline>
          </motion.div>
          <motion.h2 {...fadeUp} className="mt-6 text-4xl md:text-6xl font-black lowercase tracking-tighter leading-[1.0] text-white max-w-2xl">
            everything a gateway does.
            <br />
            <span className="text-neutral-500">nothing a bank would.</span>
          </motion.h2>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-5" data-testid="bento-grid">
            {/* A — the page */}
            <Bento className="md:col-span-7 p-8 md:p-12" testId="bento-page">
              <div
                aria-hidden
                className="absolute -top-24 -right-24 w-[380px] h-[380px] rounded-full pointer-events-none"
                style={{ background: "radial-gradient(closest-side, rgba(255,255,255,0.07), transparent 70%)" }}
              />
              <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                <div>
                  <Globe size={20} className="text-neutral-500" />
                  <h3 className="mt-5 text-2xl md:text-3xl font-bold lowercase tracking-tight text-white">your storefront, live in sixty seconds</h3>
                  <p className="mt-4 text-[14.5px] text-neutral-400 leading-relaxed">
                    claim a handle, paste the wallets you already own, share one
                    link. customers scan, pick a coin, and pay — lagos to
                    berlin, no account needed.
                  </p>
                  <div className="mt-7 flex items-center gap-4">
                    <Link to="/signup" data-testid="bento-page-cta" className="inline-flex items-center gap-2 whitespace-nowrap text-[13px] font-bold lowercase text-white border border-white/20 rounded-lg px-6 h-11 hover:bg-white hover:text-black transition-colors">
                      claim your page <ArrowRight size={13} />
                    </Link>
                    <Link to="/sell-products-for-crypto" data-testid="bento-products-link" className="mono text-[11px] text-neutral-400 hover:text-white transition-colors inline-flex items-center whitespace-nowrap py-3.5 -my-3.5">
                      sell products →
                    </Link>
                  </div>
                </div>
                <PaymentPreview />
              </div>
            </Bento>

            {/* B — non-custodial */}
            <Bento className="md:col-span-5 min-h-[420px] flex flex-col justify-end" testId="bento-custody">
              <img src={SHIELD_IMG} alt="holographic security shield illustrating non-custodial protection" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-45 hover:opacity-60 transition-opacity duration-700" />
              <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
              <div className="relative p-8 md:p-10">
                <ShieldCheck size={20} className="text-emerald-400" />
                <h3 className="mt-4 text-2xl md:text-3xl font-bold lowercase tracking-tight text-white">custody: none.</h3>
                <p className="mt-3 text-[14.5px] text-neutral-300 leading-relaxed max-w-sm">
                  no balance, no withdrawal button, no freeze switch. payments
                  go wallet-to-wallet — there's nothing of yours for us to lose.
                </p>
              </div>
            </Bento>

            {/* C — 0% */}
            <Bento className="md:col-span-4 p-8 md:p-10" testId="bento-zero">
              <Zap size={20} className="text-neutral-500" />
              <div className="mt-6 text-7xl md:text-8xl font-black tracking-tighter text-white">0%</div>
              <h3 className="mt-2 text-lg font-bold lowercase tracking-tight text-white">per transaction. forever.</h3>
              <p className="mt-3 text-[13.5px] text-neutral-500 leading-relaxed">
                legacy coinbase commerce took 1% before its 2026 retirement. bitpay takes 2% + 25¢. we take nothing — paid
                plans unlock tools, never a cut of your money.
              </p>
            </Bento>

            {/* D — tracking */}
            <Bento className="md:col-span-4 p-8 md:p-10" testId="bento-tracking">
              <Radio size={20} className="text-neutral-500" />
              <h3 className="mt-6 text-lg font-bold lowercase tracking-tight text-white">real-time on-chain tracking</h3>
              <p className="mt-3 text-[13.5px] text-neutral-500 leading-relaxed">
                every payment detected, confirmed and receipted across 13 networks
                — with whale alerts and instant email receipts.
              </p>
              <Link to="/crypto-invoicing" data-testid="bento-invoicing-link" className="mt-4 inline-flex items-center py-3.5 -my-3.5 mono text-[11px] text-neutral-400 hover:text-white transition-colors">
                how invoicing works →
              </Link>
              <div className="mt-6 flex items-end gap-1.5 h-16" aria-hidden>
                {[35, 55, 40, 70, 52, 85, 62, 95, 74, 100].map((h, i) => (
                  <div key={i} className="flex-1 rounded-t-sm bg-white/15 hover:bg-white/40 transition-colors" style={{ height: `${h}%` }} />
                ))}
              </div>
            </Bento>

            {/* E — subscriptions */}
            <Bento className="md:col-span-4 p-8 md:p-10" testId="bento-subs">
              <RefreshCcw size={20} className="text-neutral-500" />
              <h3 className="mt-6 text-lg font-bold lowercase tracking-tight text-white">crypto subscriptions</h3>
              <p className="mt-3 text-[13.5px] text-neutral-500 leading-relaxed">
                recurring plans, paid on-chain. memberships, retainers and
                saas billing — settled straight to your wallet.
              </p>
              <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/10 px-3 lg:px-4 py-2 font-mono text-[10px] lg:text-[11px] text-neutral-300 whitespace-nowrap max-w-full">
                <LineChart size={12} className="text-emerald-400 shrink-0" />
                <span className="md:hidden lg:inline">mrr, live from the chain</span>
                <span className="hidden md:inline lg:hidden">live mrr</span>
              </div>
            </Bento>
          </div>
        </div>
      </section>

      {/* ══ 3b · MID CTA ══════════════════════════════════════════ */}
      <MidCta id="platform" />

      {/* ══ 4 · DEVELOPERS ════════════════════════════════════════ */}
      <section className="relative py-24 md:py-40 border-t border-white/10 overflow-hidden">
        <img src={DOTS_IMG} alt="dark abstract network nodes texture" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-[0.12]" />
        <div aria-hidden className="absolute inset-0 bg-gradient-to-b from-black via-transparent to-black" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <motion.div {...fadeUp}>
            <Overline>developers</Overline>
            <h2 className="mt-6 text-4xl md:text-6xl font-black lowercase tracking-tighter leading-[1.0] text-white">
              a gateway that
              <br />
              <span className="text-neutral-500">speaks curl.</span>
            </h2>
            <p className="mt-7 text-base text-neutral-400 leading-relaxed max-w-md">
              stripe-style orders api, hosted checkout, hmac-signed webhooks
              and embeddable widgets. one key, one request, and your product
              accepts crypto.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Cta to="/crypto-payment-gateway" testId="dev-gateway-cta" solid className="sm:!px-6">explore the gateway <ArrowUpRight size={14} /></Cta>
              <Cta to="/docs" testId="dev-docs-cta" className="sm:!px-6">read the docs</Cta>
              <Cta to="/integrations" testId="dev-integrations-cta" className="sm:!px-6">integrations</Cta>
            </div>
          </motion.div>
          <motion.div {...fadeUp}>
            <CodeWindow />
          </motion.div>
        </div>
      </section>

      {/* ══ 5 · HOW IT WORKS ══════════════════════════════════════ */}
      <section className="py-24 md:py-40 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 md:px-12">
          <motion.div {...fadeUp}>
            <Overline>how it works</Overline>
          </motion.div>
          <motion.h2 {...fadeUp} className="mt-6 text-4xl md:text-6xl font-black lowercase tracking-tighter leading-[1.0] text-white">
            sixty seconds<span className="text-neutral-500"> to live.</span>
          </motion.h2>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="steps-grid">
            {STEPS.map((s, i) => (
              <motion.div
                {...fadeUp}
                key={s.n}
                data-testid={`step-${s.n}`}
                className="group relative rounded-3xl border border-white/10 bg-white/[0.02] p-8 md:p-6 lg:p-10 hover:border-white/25 hover:bg-white/[0.04] transition-colors duration-500"
              >
                <span aria-hidden className="absolute top-3 right-4 mono text-[11px] text-zinc-700 select-none">+</span>
                <span aria-hidden className="absolute bottom-3 left-4 mono text-[11px] text-zinc-700 select-none">+</span>
                <div className="flex items-center justify-between gap-3">
                  <span className="inline-flex items-center justify-center w-12 h-12 md:w-10 md:h-10 lg:w-12 lg:h-12 rounded-full bg-white text-black font-mono text-[13px] font-bold group-hover:scale-110 transition-transform duration-500 shrink-0">{s.n}</span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] md:tracking-[0.1em] lg:tracking-[0.2em] text-zinc-600 border border-zinc-800 rounded-full px-3 md:px-2.5 lg:px-3 py-1.5 whitespace-nowrap shrink-0">{["~10 sec", "~30 sec", "~20 sec"][i]}</span>
                </div>
                <h3 className="mt-8 text-xl font-black lowercase tracking-tight text-white">{s.t}</h3>
                <p className="mt-3 text-[14px] text-neutral-400 leading-relaxed">{s.c}</p>
                <div className="grid grid-rows-[0fr] group-hover:grid-rows-[1fr] opacity-0 group-hover:opacity-100 transition-[grid-template-rows,opacity] duration-500" data-testid={`step-snippet-${i + 1}`}>
                  <div className="overflow-hidden">
                    {i === 0 && (
                      <div className="mt-5 rounded-xl border border-white/10 bg-black/60 px-4 py-3 font-mono text-[12px] lowercase flex items-center">
                        <span className="text-zinc-500">crypt.pe/</span>
                        <span className="text-white">you</span>
                        <span aria-hidden className="ml-px text-white animate-pulse">▌</span>
                      </div>
                    )}
                    {i === 1 && (
                      <div className="mt-5 rounded-xl border border-white/10 bg-black/60 px-4 py-3 font-mono text-[12px] lowercase flex items-center justify-between gap-2">
                        <span className="text-zinc-300 truncate">0x7a3f…9c21</span>
                        <span className="text-white shrink-0">✓ ethereum</span>
                      </div>
                    )}
                    {i === 2 && (
                      <div className="mt-5 rounded-xl border border-white/10 bg-black/60 px-4 md:px-3 lg:px-4 py-3 font-mono text-[12px] md:text-[11px] lg:text-[12px] lowercase flex items-center gap-3.5 md:gap-2.5 lg:gap-3.5">
                        <span aria-hidden className="grid grid-cols-4 gap-[3px] shrink-0">
                          {[1,0,1,1,0,1,1,0,1,1,0,1,1,0,1,1].map((on, d) => (
                            <span key={d} style={on ? { animationDelay: `${d * 28}ms` } : undefined} className={`w-[4px] h-[4px] rounded-[1px] ${on ? "qr-dot bg-white" : "bg-white/15"}`} />
                          ))}
                        </span>
                        <span className="text-zinc-300 whitespace-nowrap">scan → <span className="text-white">paid ✓</span></span>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          <motion.div {...fadeUp} className="mt-6 flex items-center justify-end gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-600">
            <span aria-hidden className="w-8 h-px bg-zinc-800" /> total ≈ sixty seconds. no identity checks to start
          </motion.div>
          <motion.div {...fadeUp} data-testid="landing-switch-links" className="mt-10 flex flex-nowrap items-center gap-2 overflow-x-auto no-scrollbar font-mono text-[11px] lowercase">
            <span className="text-zinc-600 shrink-0">switching?</span>
            {[["stripe", "stripe"], ["paypal", "paypal"], ["square", "square"], ["bank wire", "wire"], ["regional gateways", "regional"]].map(([label, id]) => (
              <Link key={id} to={`/switch?from=${id}`} data-testid={`landing-switch-${id}`} className="shrink-0 whitespace-nowrap text-zinc-400 hover:text-white border border-zinc-800 hover:border-zinc-600 rounded-full px-3.5 py-1.5 transition-colors">
                from {label} →
              </Link>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ══ 5b · FOR YOUR BUSINESS — vertical teaser into the hub ═ */}
      <section className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 md:px-12 py-16 md:py-24">
          <motion.div {...fadeUp} className="flex flex-wrap items-end justify-between gap-4 mb-12">
            <div>
              <Overline>for your business</Overline>
              <h2 className="mt-4 text-3xl sm:text-4xl font-black lowercase tracking-tighter text-white">
                whatever you sell, get paid in crypto.
              </h2>
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <span data-testid="landing-business-todays-picks" className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 border border-zinc-800 rounded-full px-3.5 py-1.5">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white/60 opacity-60" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" />
                </span>
                today's picks · rotates daily
              </span>
              <Link to="/accept-crypto-for" data-testid="landing-business-hub-link" className="font-mono text-[12px] text-zinc-500 hover:text-white transition-colors lowercase inline-flex items-center gap-1.5 py-2">
                all {BUSINESS_PAGES.length} business types <ArrowRight size={12} />
              </Link>
            </div>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="landing-business-teaser">
            {Array.from({ length: 4 }, (_, i) =>
              BUSINESS_PAGES[(Math.floor(Date.now() / 86400000) + i * 3) % BUSINESS_PAGES.length]
            ).map((b) => (
              <motion.div {...fadeUp} key={b.slug}>
                <Link
                  to={`/accept-crypto-for/${b.slug}`}
                  data-testid={`landing-business-${b.slug}`}
                  className="group relative flex flex-col h-full rounded-3xl bg-[#0A0A0A] border border-white/10 hover:bg-[#EDEDEF] hover:border-[#EDEDEF] transition-colors duration-500 p-7"
                >
                  <span aria-hidden className="absolute top-3 right-4 mono text-[11px] text-zinc-800 group-hover:text-neutral-400 transition-colors duration-500 select-none">+</span>
                  <Overline className="!text-[10px]">for</Overline>
                  <div className="mt-2 text-lg font-black lowercase tracking-tight text-white group-hover:text-black transition-colors duration-500">{b.noun}</div>
                  <p className="mt-2.5 text-[13px] text-neutral-400 group-hover:text-neutral-700 transition-colors duration-500 leading-relaxed flex-1">{b.hero}.</p>
                  <span className="mt-5 inline-flex items-center gap-1.5 font-mono text-[10.5px] text-zinc-600 group-hover:text-black transition-colors duration-500 lowercase">
                    read the setup <ArrowRight size={10} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ 6 · PROOF ═════════════════════════════════════════════ */}
      <section className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 md:px-12 py-16 md:py-24">
          <motion.div {...fadeUp} className="text-center mb-14">
            <Overline>the proof writes itself</Overline>
          </motion.div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-10 text-center" data-testid="stats-band">
            {[
              ...(stats?.volume_usd > 0
                ? [{ v: usd(stats.volume_usd), l: "processed on-chain" }]
                : [{ v: "0%", l: "platform fee. forever" }]),
              ...(stats?.pages_claimed > 0 ? [{ v: compact(stats.pages_claimed), l: "payment pages" }] : []),
              ...(stats?.payments_total > 0 ? [{ v: compact(stats.payments_total), l: "payments tracked" }] : []),
              { v: String(COIN_COUNT), l: `coins · ${NETWORK_COUNT} networks` },
              ...(stats?.pages_claimed > 0 && stats?.payments_total > 0 ? [] : [{ v: "60s", l: "to go live" }]),
            ].slice(0, 4).map((s) => (
              <div key={s.l}>
                <div className="text-5xl sm:text-7xl font-black tracking-tighter text-white">{s.v}</div>
                <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-600">{s.l}</div>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-600" data-testid="stats-honesty-note">
            counters show live verified numbers only — nothing projected, nothing padded
          </div>
          <div className="mt-5 text-center">
            <Link
              to="/best-crypto-payment-gateway"
              data-testid="home-best-gateways-link"
              className="font-mono text-[11px] lowercase text-zinc-500 hover:text-white underline underline-offset-4 decoration-zinc-800 hover:decoration-white transition-colors"
            >
              see how we stack up — best crypto payment gateways in 2026 →
            </Link>
          </div>
        </div>
      </section>

      {/* ══ 6b · COMMON QUESTIONS — editorial grid, not an accordion ═ */}
      <section className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 md:px-12 py-16 md:py-24">
          <motion.div {...fadeUp} className="mb-14">
            <Overline>common questions</Overline>
            <h2 className="mt-4 text-3xl sm:text-4xl font-black lowercase tracking-tighter text-white">
              everything merchants ask before switching.
            </h2>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-x-14 gap-y-12" data-testid="landing-faq">
            {LANDING_FAQ.map((f, i) => (
              <motion.div {...fadeUp} key={f.q}>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-600">0{i + 1}</div>
                <h3 className="mt-3 text-[16px] font-bold lowercase tracking-tight text-zinc-100">{f.q}</h3>
                <p className="mt-2.5 text-[13.5px] text-zinc-500 leading-relaxed">{f.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ 6c · TESTIMONIALS (renders only with approved reviews) ═ */}
      <React.Suspense fallback={null}><Testimonials /></React.Suspense>

      {/* ══ 7 · FINAL CTA ═════════════════════════════════════════ */}
      <section className="relative py-28 md:py-48 border-t border-white/10 text-center overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-x-0 top-0 h-full pointer-events-none"
          style={{ background: "radial-gradient(800px 400px at 50% 100%, rgba(255,255,255,0.06), transparent 70%)" }}
        />
        <div className="relative max-w-3xl mx-auto px-5 md:px-12">
          <motion.h2
            {...fadeUp}
            data-testid="final-cta-title"
            className="text-5xl md:text-8xl font-black lowercase tracking-tighter leading-[0.95] text-white"
          >
            your handle
            <br />
            <span className="text-neutral-500">is waiting.</span>
          </motion.h2>
          <motion.p {...fadeUp} className="mt-8 text-base md:text-lg text-neutral-400 max-w-md mx-auto">
            free forever. no custody. no cut.
          </motion.p>
          <motion.div {...fadeUp} className="mt-12">
            <ClaimForm testPrefix="final" />
          </motion.div>
        </div>
      </section>


      <Footer />
    </main>
  );
}
